import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { eventKeys } from './useEvents.ts'

/**
 * Tickets, registrations and the waitlist.
 *
 * Everything is keyed by the event *instance* — people register for the 24th,
 * not for "Carols" in general — and a cancellation invalidates the whole
 * instance, because promoting the waitlist changes rows the caller never named.
 */

export const registrationKeys = {
  tickets: (instanceId: string) => [...eventKeys.all(), 'tickets', instanceId] as const,
  registrations: (instanceId: string) => [...eventKeys.all(), 'registrations', instanceId] as const,
  mine: () => [...eventKeys.all(), 'registrations', 'mine'] as const,
}

/**
 * What I have signed up for — the only registration list a plain member may
 * read. Cancelling from here invalidates the whole events tree, because giving
 * a place back can promote somebody and changes what the date has left.
 */
export function useMyRegistrations() {
  const api = useApi()
  return useQuery({
    queryKey: registrationKeys.mine(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.registrations.mine.get()
      if (error) throw error
      return data
    },
  })
}

export type MyRegistration = NonNullable<ReturnType<typeof useMyRegistrations>['data']>[number]

/** Cancelling my own, from the list of mine. */
export function useCancelMyRegistration() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (registrationId: string) => {
      const { data, error } = await api.api.v1.registrations({ id: registrationId }).delete()
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: eventKeys.all() }),
  })
}

export function useTickets(instanceId: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: registrationKeys.tickets(instanceId),
    enabled: enabled && instanceId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.events.instances({ id: instanceId }).tickets.get()
      if (error) throw error
      return data
    },
  })
}

export type Ticket = NonNullable<ReturnType<typeof useTickets>['data']>[number]

/** Staff only — an attendee list is other people's names and phone numbers. */
export function useRegistrations(instanceId: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: registrationKeys.registrations(instanceId),
    enabled: enabled && instanceId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.events
        .instances({ id: instanceId })
        .registrations.get()
      if (error) throw error
      return data
    },
  })
}

export type Registration = NonNullable<ReturnType<typeof useRegistrations>['data']>[number]

function useRegistrationInvalidation(instanceId: string) {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: registrationKeys.tickets(instanceId) })
    void queryClient.invalidateQueries({ queryKey: registrationKeys.registrations(instanceId) })
    // And my own list. Registering changes what *I* am signed up for, and
    // leaving this out meant the mobile screen said "you are on the waiting
    // list" while the section above it still showed nothing booked.
    void queryClient.invalidateQueries({ queryKey: registrationKeys.mine() })
  }
}

export function useCreateTicket(instanceId: string) {
  const api = useApi()
  const invalidate = useRegistrationInvalidation(instanceId)
  return useMutation({
    mutationFn: async (body: {
      name: string
      description?: string | null
      capacity?: number | null
      /** Integer cents; zero is free. */
      priceCents?: number
      maxPerRegistration?: number
      opensAt?: string | null
      closesAt?: string | null
      questions?: { label: string; type?: string; required?: boolean }[]
    }) => {
      const { data, error } = await api.api.v1.events
        .instances({ id: instanceId })
        .tickets.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => invalidate(),
  })
}

export interface RegisterInput {
  registrationTypeId: string
  contactName: string
  contactEmail?: string | null
  contactPhone?: string | null
  personId?: string | null
  quantity?: number
  notes?: string | null
  attendees?: { name: string; personId?: string | null; answers?: Record<string, unknown> }[]
}

/**
 * Takes a place, or a place in the queue. The reply says which — screens show
 * that plainly rather than a generic "done", because the difference is the
 * whole thing the person wanted to know.
 */
export function useRegister(instanceId: string) {
  const api = useApi()
  const invalidate = useRegistrationInvalidation(instanceId)
  return useMutation({
    mutationFn: async (body: RegisterInput) => {
      const { data, error } = await api.api.v1.registrations.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => invalidate(),
  })
}

/**
 * Cancelling can promote somebody else, so the whole instance is refetched
 * rather than the one row the caller touched.
 */
export function useCancelRegistration(instanceId: string) {
  const api = useApi()
  const invalidate = useRegistrationInvalidation(instanceId)
  return useMutation({
    mutationFn: async (registrationId: string) => {
      const { data, error } = await api.api.v1.registrations({ id: registrationId }).delete()
      if (error) throw error
      return data
    },
    onSuccess: () => invalidate(),
  })
}

/**
 * Records what has been paid towards a registration — the total so far, not
 * an increment, so saving the same amount twice changes nothing.
 */
export function useRecordPayment(instanceId: string) {
  const api = useApi()
  const invalidate = useRegistrationInvalidation(instanceId)
  return useMutation({
    mutationFn: async ({ id, paidCents }: { id: string; paidCents: number }) => {
      const { data, error } = await api.api.v1.registrations({ id }).payment.patch({ paidCents })
      if (error) throw error
      return data
    },
    onSuccess: () => invalidate(),
  })
}
